import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  chakra,
  Flex,
  Grid,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { GripVertical } from 'lucide-react';
import { useState, useMemo, useCallback, useRef, memo } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

import type { AdminCourseScheduleItemType } from '@duke-hq/shared-constants/course';
import { isDefined } from '@duke-hq/lib';
import { parseAdminCourseSchedule } from './utils';
import { groupScheduleItems } from './scheduleGrouping';

const ReorderGroup = chakra(Reorder.Group);
const ReorderItem = chakra(Reorder.Item);

const GROUPED_CONTENT_COLUMNS = `${pxToRem(24)} ${pxToRem(80)} ${pxToRem(72)} ${pxToRem(72)} 1fr`;

const DURATION_PATTERN = /^[1-9]$/;

function TypeBadge({ type }: { type: AdminCourseScheduleItemType['type'] }) {
  const config =
    type === 'UNIT'
      ? { label: t`Unit`, colorScheme: 'purple' }
      : type === 'CONVERSATION'
        ? { label: t`Conversation`, colorScheme: 'blue' }
        : { label: t`Assessment`, colorScheme: 'green' };

  return (
    <Badge
      colorScheme={config.colorScheme}
      variant="subtle"
      textTransform="none"
      fontWeight="medium"
    >
      {config.label}
    </Badge>
  );
}

type GroupedScheduleRowProps = {
  item: AdminCourseScheduleItemType;
  index: number;
  isHead: boolean;
  isTail: boolean;
  displaySemester: string;
  displayWeek: string;
  displayDuration: string;
  isSuperseded: boolean;
  handleSemesterChange: (itemIndex: number, itemId: string, newSemester: string) => void;
  handleChangeWhileEditing: (
    itemId: string,
    field: 'Week' | 'Semester' | 'Duration',
    value: string,
  ) => void;
  handleBlur: (index: number) => void;
};

// The grouped-by-unit view. Each conversation/assessment row carries its own Duration input beside
// Week; unit rows have no duration.
const GroupedScheduleRow = memo(function GroupedScheduleRow({
  item,
  index,
  isHead,
  isTail,
  displaySemester,
  displayWeek,
  displayDuration,
  isSuperseded,
  handleSemesterChange,
  handleChangeWhileEditing,
  handleBlur,
}: GroupedScheduleRowProps) {
  const dragControls = useDragControls();
  const isUnit = item.type === 'UNIT';
  const durationInvalid = !isUnit && !DURATION_PATTERN.test(displayDuration);

  return (
    <ReorderItem value={item} position="relative" dragListener={false} dragControls={dragControls}>
      <Box
        mt={isHead ? 3 : 0}
        overflow="hidden"
        borderWidth="1px"
        borderBottomWidth={isTail ? '1px' : '0'}
        borderTopRadius={isHead ? 'md' : '0'}
        borderBottomRadius={isTail ? 'md' : '0'}
        borderColor={isSuperseded ? 'orange.300' : 'border.default'}
        opacity={isSuperseded ? 0.8 : 1}
      >
        <Grid
          templateColumns={GROUPED_CONTENT_COLUMNS}
          gap={5}
          p={2}
          pl={isUnit ? 2 : 5}
          alignItems="center"
          bg={isSuperseded ? 'orange.50' : isUnit ? 'bg.canvas' : 'bg.subtle'}
          _hover={{ bg: isSuperseded ? 'orange.100' : 'bg.muted' }}
        >
          <Box display="flex" alignItems="center">
            <IconButton
              _hover={{ color: 'brand.600' }}
              icon={<Icon as={GripVertical} />}
              cursor="grab"
              size="sm"
              variant="tertiary"
              aria-label={t`Reorder schedule item`}
              onPointerDown={(e) => dragControls.start(e)}
            />
          </Box>
          <Box display="flex" alignItems="center">
            <Select
              size="sm"
              value={displaySemester}
              onChange={(e) => handleSemesterChange(index, item.id, e.target.value)}
              w="full"
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </Select>
          </Box>
          <Box display="flex" alignItems="center">
            <NumberInput
              size="sm"
              min={1}
              max={99}
              value={displayWeek}
              onChange={(valueString) => handleChangeWhileEditing(item.id, 'Week', valueString)}
              onBlur={() => handleBlur(index)}
              w="full"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </Box>
          <Box display="flex" alignItems="center">
            {isUnit ? null : (
              <NumberInput
                size="sm"
                min={1}
                max={9}
                value={displayDuration}
                isInvalid={durationInvalid}
                onChange={(valueString) => {
                  // Never let the duration hold a value below one: ignore keystrokes that would
                  // make it zero, negative, or non-numeric. Empty is allowed so the field can be
                  // cleared and retyped, then normalised on blur.
                  if (valueString === '' || DURATION_PATTERN.test(valueString)) {
                    handleChangeWhileEditing(item.id, 'Duration', valueString);
                  }
                }}
                onBlur={() => handleBlur(index)}
                title={durationInvalid ? t`Enter a whole number of weeks` : undefined}
                w="full"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={2} overflow="hidden">
            {isUnit ? (
              <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                {item['Unit Code']}
              </Text>
            ) : null}
            <Text
              fontSize="sm"
              minW={0}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              color={isSuperseded ? 'fg.muted' : isUnit ? undefined : 'fg.muted'}
              textDecoration={isSuperseded ? 'line-through' : undefined}
            >
              {item.Title}
            </Text>
            <Box flexShrink={0}>
              <TypeBadge type={item.type} />
            </Box>
          </Box>
        </Grid>
      </Box>
    </ReorderItem>
  );
});

type BulkUpdateModalState = {
  isOpen: boolean;
  itemIndex: number;
  newSemester: string;
  modalType: 'followingItems' | 'llnUnits';
};

function BulkSemesterModal({
  state,
  onClose,
  onApply,
}: {
  state: BulkUpdateModalState | null;
  onClose: () => void;
  onApply: (applyToAll: boolean) => void;
}) {
  return (
    <Modal isOpen={state?.isOpen ?? false} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Update Semester</Trans>
        </ModalHeader>
        <ModalBody>
          {state?.modalType === 'followingItems' ? (
            <Text>
              <Trans>
                Do you want to apply this semester change to all following items, or just this item?
              </Trans>
            </Text>
          ) : (
            <Text>
              <Trans>Do you want to apply semester 0 to all LLN units, or just this item?</Trans>
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="tertiary" onClick={() => onApply(false)}>
              <Trans>This item only</Trans>
            </Button>
            <Button colorScheme="brand" onClick={() => onApply(true)}>
              {state?.modalType === 'followingItems' ? (
                <Trans>All following items</Trans>
              ) : (
                <Trans>All LLN units</Trans>
              )}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

type CourseScheduleEditorProps = {
  value: string;
  onChange: (json: string) => void;
  courseIdentifier?: string;
  supersededUnitIds?: Set<string>;
};

export function CourseScheduleEditor({
  value,
  onChange,
  supersededUnitIds = new Set(),
}: CourseScheduleEditorProps) {
  const [editingValues, setEditingValues] = useState<
    Record<string, { Week?: string; Semester?: string; Duration?: string }>
  >({});
  const [bulkUpdateModal, setBulkUpdateModal] = useState<BulkUpdateModalState | null>(null);

  const scheduleItems = useMemo(() => parseAdminCourseSchedule(value), [value]);
  // Flatten the unit blocks back into render order, tagging each row as the head/tail of its block
  // so a unit and its items read as one card. groupScheduleItems preserves order, so this lines up
  // one-to-one with scheduleItems and the index below still indexes scheduleItems.
  const groupedRows = useMemo(
    () =>
      groupScheduleItems(scheduleItems).flatMap((block) => {
        const blockRows = [block.unit, ...block.children].filter(isDefined);
        return blockRows.map((item, i) => ({
          item,
          isHead: i === 0,
          isTail: i === blockRows.length - 1,
        }));
      }),
    [scheduleItems],
  );

  const commitItems = useCallback(
    (items: AdminCourseScheduleItemType[]) => {
      onChange(JSON.stringify(items));
    },
    [onChange],
  );

  const handleSemesterChange = useCallback(
    (itemIndex: number, itemId: string, newSemester: string) => {
      const currentSemester = scheduleItems[itemIndex]?.Semester;

      // If semester changed, show bulk update modal based on the selected semester
      if (currentSemester !== newSemester) {
        // For semester 2, ask to apply to all following items
        if (newSemester === '2') {
          setBulkUpdateModal({
            isOpen: true,
            itemIndex,
            newSemester,
            modalType: 'followingItems',
          });
        } else if (newSemester === '0') {
          // For semester 0, ask to apply to all LLN units
          setBulkUpdateModal({
            isOpen: true,
            itemIndex,
            newSemester,
            modalType: 'llnUnits',
          });
        } else {
          // Otherwise just update the current item
          const newItems = [...scheduleItems];
          const item = newItems[itemIndex];
          if (isDefined(item)) {
            newItems[itemIndex] = { ...item, Semester: newSemester };
            commitItems(newItems);
          }
        }
      } else {
        // Same semester, just update the current item
        setEditingValues((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], Semester: newSemester },
        }));
      }
    },
    [scheduleItems, commitItems],
  );

  const applyBulkSemesterUpdate = useCallback(
    (applyToAll: boolean) => {
      if (bulkUpdateModal === null) {
        return;
      }

      const { itemIndex, newSemester, modalType } = bulkUpdateModal;
      const newItems = [...scheduleItems];

      if (applyToAll) {
        // Update current item and all following items
        if (modalType === 'followingItems') {
          for (let i = itemIndex; i < newItems.length; i++) {
            const item = newItems[i];
            if (isDefined(item)) {
              newItems[i] = { ...item, Semester: newSemester };
            }
          }
        } else if (modalType === 'llnUnits') {
          // Update all items with unit code "LLN"
          for (let i = 0; i < newItems.length; i++) {
            const item = newItems[i];
            if (i !== itemIndex && isDefined(item) && item['Unit Code'] === 'LLN') {
              newItems[i] = { ...item, Semester: newSemester };
            }
          }
        }
      } else {
        // Update only current item
        const item = newItems[itemIndex];
        if (isDefined(item)) {
          newItems[itemIndex] = { ...item, Semester: newSemester };
        }
      }

      commitItems(newItems);
      setBulkUpdateModal(null);
      setEditingValues({});
    },
    [bulkUpdateModal, scheduleItems, commitItems],
  );

  const handleChangeWhileEditing = useCallback(
    (itemId: string, field: 'Week' | 'Semester' | 'Duration', value: string) => {
      setEditingValues((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));
    },
    [],
  );

  // Refs let handleBlur read the latest schedule and edits without being re-created on every
  // keystroke; that stable identity lets the memoised rows below skip re-rendering while a
  // different row is being edited.
  const scheduleItemsRef = useRef(scheduleItems);
  scheduleItemsRef.current = scheduleItems;
  const editingValuesRef = useRef(editingValues);
  editingValuesRef.current = editingValues;

  const handleBlur = useCallback(
    (index: number) => {
      const items = scheduleItemsRef.current;
      const item = items[index];
      if (!isDefined(item)) {
        return;
      }

      const editingValue = editingValuesRef.current[item.id];
      if (!isDefined(editingValue)) {
        return;
      }

      const newItems = [...items];
      const currentItem = newItems[index];
      if (!isDefined(currentItem)) {
        return;
      }

      // An invalid duration — blank, zero, or not a whole number of at least one — can't be
      // committed: the whole schedule is parsed as one array, so a single value the schema rejects
      // makes every row disappear. DURATION_PATTERN mirrors that schema rule; fall back to the
      // row's last valid duration, or one week.
      const rawDuration = editingValue.Duration;
      const durationWeeks =
        rawDuration === undefined
          ? undefined
          : DURATION_PATTERN.test(rawDuration)
            ? rawDuration
            : currentItem.durationWeeks && DURATION_PATTERN.test(currentItem.durationWeeks)
              ? currentItem.durationWeeks
              : '1';

      newItems[index] = {
        ...currentItem,
        ...(editingValue.Week !== undefined && { Week: editingValue.Week }),
        ...(editingValue.Semester !== undefined && { Semester: editingValue.Semester }),
        ...(durationWeeks !== undefined && { durationWeeks }),
      };

      setEditingValues((prev) => {
        const next = { ...prev };
        next[item.id] = {};
        return next;
      });

      commitItems(newItems);
    },
    [commitItems],
  );

  const handleReorder = useCallback(
    (newOrder: AdminCourseScheduleItemType[]) => {
      commitItems(newOrder);
    },
    [commitItems],
  );

  if (scheduleItems.length === 0) {
    return (
      <Box p={4} borderWidth="1px" borderRadius="md" borderColor="border.default">
        <Text color="fg.muted">
          <Trans>No schedule items found.</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch" pr={2}>
      <Box>
        <Flex
          position="sticky"
          top="var(--course-schedule-sticky-top, 0px)"
          zIndex={1}
          bg="bg.surface"
          gap={5}
          px={2}
          py={2}
          fontSize="xs"
          textTransform="uppercase"
          color="fg.muted"
          fontWeight="medium"
          borderBottomWidth="1px"
          borderColor="border.default"
        >
          <Grid flex={1} templateColumns={GROUPED_CONTENT_COLUMNS} gap={5}>
            <Box></Box>
            <Box>
              <Trans>Semester</Trans>
            </Box>
            <Box>
              <Trans>Week</Trans>
            </Box>
            <Box>
              <Trans>Duration</Trans>
            </Box>
            <Box>
              <Trans>Title</Trans>
            </Box>
          </Grid>
        </Flex>

        <ReorderGroup values={scheduleItems} onReorder={handleReorder} listStyleType="none">
          <VStack spacing={0} align="stretch" pb={12}>
            {groupedRows.map(({ item, isHead, isTail }, index) => {
              const editingValue = editingValues[item.id];
              const displaySemester = editingValue?.Semester ?? item.Semester;
              const displayWeek = editingValue?.Week ?? item.Week;
              const displayDuration = editingValue?.Duration ?? item.durationWeeks ?? '1';

              return (
                <GroupedScheduleRow
                  key={item.id}
                  item={item}
                  index={index}
                  isHead={isHead}
                  isTail={isTail}
                  displaySemester={displaySemester}
                  displayWeek={displayWeek}
                  displayDuration={displayDuration}
                  isSuperseded={supersededUnitIds.has(item.unitId)}
                  handleSemesterChange={handleSemesterChange}
                  handleChangeWhileEditing={handleChangeWhileEditing}
                  handleBlur={handleBlur}
                />
              );
            })}
          </VStack>
        </ReorderGroup>
      </Box>

      <BulkSemesterModal
        state={bulkUpdateModal}
        onClose={() => setBulkUpdateModal(null)}
        onApply={applyBulkSemesterUpdate}
      />
    </VStack>
  );
}
